import React from 'react';
import {
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import styled from 'styled-components';

const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
`;

const Gradient = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 48px;
  pointer-events: none;
  z-index: 1;
`;

const TopGradient = styled(Gradient)`
  top: 0;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
`;

const BottomGradient = styled(Gradient)`
  bottom: 0;
  background: linear-gradient(to top, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
`;

const StyledHeader = styled(DialogTitle)`
  display: grid;
  grid-column-gap: 2.5rem;
  grid-template-columns: 1fr auto;
  align-items: center;

  a {
    font-size: 22px;
    align-self: start;
    margin-top: -1.5rem;
    margin-right: -2.5rem;

    @media (max-width: 600px) {
      margin-top: -1rem;
      margin-right: -0.5rem;
    }
  }

  @media (max-width: 600px) {
    font-size: 24px;
    padding-bottom: 1rem;
  }
`;

/**
 * Shared dialog component wrapping MUI Dialog.
 *
 * Props:
 *   scrollable — pass this when the dialog content may be taller than the
 *   viewport. The dialog is constrained to calc(100% - 64px) and the content
 *   area scrolls while the header and action buttons stay fixed. White fade
 *   gradients are shown automatically at the top and bottom edges to indicate
 *   there is more content to scroll to.
 */
export const Dialog = ({
  children,
  open,
  onClose,
  header,
  actions,
  id,
  maxWidth,
  fullWidth,
  actionsSx,
  contentSx,
  scrollable,
}) => {
  const contentRef = React.useRef(null);
  const [showTopGradient, setShowTopGradient] = React.useState(false);
  const [showBottomGradient, setShowBottomGradient] = React.useState(false);

  const updateGradients = () => {
    const el = contentRef.current;
    if (!el) return;
    setShowTopGradient(el.scrollTop > 0);
    setShowBottomGradient(el.scrollTop + el.clientHeight < el.scrollHeight);
  };

  React.useEffect(() => {
    updateGradients();
  });

  return (
    <MuiDialog
      aria-labelledby={id}
      aria-describedby={id}
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      scroll={scrollable ? 'paper' : undefined}
    >
      <StyledHeader>
        {React.isValidElement(header) ? header : <div>{header}</div>}
        <a onClick={onClose}>
          <FontAwesomeIcon icon={faXmark} color="#272727" />
        </a>
      </StyledHeader>
      <ContentWrapper>
        {showTopGradient && <TopGradient />}
        <DialogContent ref={contentRef} onScroll={updateGradients} sx={contentSx}>
          {children}
        </DialogContent>
        {showBottomGradient && <BottomGradient />}
      </ContentWrapper>
      {actions && <DialogActions sx={actionsSx}>{actions}</DialogActions>}
    </MuiDialog>
  );
};
